import { useTranslation } from "react-i18next";
import { Input, Select } from "ui";

import { useMarketingTemplates } from "../../apis";
import { EMarketingChannel } from "../../marketing.types";

import styles from "./MessageComposer.module.scss";

export interface MessageComposerValue {
  messageText: string;
  templateName: string;
  templateParams: string[];
  channel: string;
}

interface MessageComposerProps {
  value: MessageComposerValue;
  onChange: (value: MessageComposerValue) => void;
  disabled?: boolean;
  hideChannel?: boolean;
}

const NO_TEMPLATE_VALUE = "none";
const AUTO_CHANNEL_VALUE = "auto";

const CHANNEL_OPTIONS = [
  { label: "Auto (push if app installed, else WhatsApp)", value: AUTO_CHANNEL_VALUE },
  { label: "WhatsApp", value: EMarketingChannel.WhatsApp },
  { label: "Push (app users)", value: EMarketingChannel.Push }
];

const PREVIEW_FIRSTNAME = "Awa";

const substituteFirstname = (text: string): string =>
  text.split("{{firstname}}").join(PREVIEW_FIRSTNAME);

const findUnknownVariables = (text: string): string[] =>
  Array.from(
    new Set(
      (text.match(/\{\{\s*\w+\s*\}\}/g) ?? [])
        .map((match) => match.replace(/[{}\s]/g, ""))
        .filter((name) => name !== "firstname")
    )
  );

const renderTemplatePreview = (bodyText: string, params: string[]): string =>
  params.reduce(
    (body, param, index) =>
      body
        .split(`{{${index + 1}}}`)
        .join(substituteFirstname(param) || `{{${index + 1}}}`),
    bodyText
  );

const MessageComposer = ({
  value,
  onChange,
  disabled,
  hideChannel
}: MessageComposerProps) => {
  const { t } = useTranslation();

  const { templates: approvedTemplates } = useMarketingTemplates(true);

  const selectedTemplate = approvedTemplates.find(
    (template) => template.name === value.templateName
  );

  const templateOptions = [
    { label: t("No template (free text only)"), value: NO_TEMPLATE_VALUE },
    ...approvedTemplates.map((template) => ({
      label: `${template.name} (${template.language})`,
      value: template.name
    }))
  ];

  const patch = (changes: Partial<MessageComposerValue>) =>
    onChange({ ...value, ...changes });

  const handleTemplateChange = (selected: string) => {
    if (selected === NO_TEMPLATE_VALUE) {
      patch({ templateName: "", templateParams: [] });
      return;
    }

    const template = approvedTemplates.find(
      (item) => item.name === selected
    );

    patch({
      templateName: selected,
      templateParams: Array.from(
        { length: template?.variableCount ?? 0 },
        () => ""
      )
    });
  };

  const handleParamChange = (index: number, paramValue: string) => {
    const params = [...value.templateParams];
    params[index] = paramValue;
    patch({ templateParams: params });
  };

  const unknownVariables = findUnknownVariables(value.messageText);

  return (
    <div className={styles.composer}>
      <Input
        label={t("Message text (used inside the 24h window)")}
        asTag="textarea"
        placeholder={t("Hello {{firstname}}, ...")}
        disabled={disabled}
        value={value.messageText}
        onChange={(event) => patch({ messageText: event.target.value })}
      />

      {value.messageText && (
        <div className={styles.composer__preview}>
          <span className={styles.composer__previewLabel}>{t("Preview")}</span>
          <p className={styles.composer__previewBody}>
            {substituteFirstname(value.messageText)}
          </p>

          {unknownVariables.length > 0 && (
            <span className={styles.composer__warning}>
              {t(
                "Only firstname is substituted — other variables are sent as-is"
              )}
              : {unknownVariables.map((name) => `{{${name}}}`).join(", ")}
            </span>
          )}
        </div>
      )}

      <Select
        label={t("Approved template (used outside the 24h window)")}
        options={templateOptions}
        value={value.templateName || NO_TEMPLATE_VALUE}
        disabled={disabled}
        onChange={(selected) => handleTemplateChange(selected as string)}
      />

      {selectedTemplate && (
        <div className={styles.composer__template}>
          <p className={styles.composer__templateBody}>
            {selectedTemplate.bodyText}
          </p>

          {value.templateParams.map((param, index) => (
            <Input
              key={index}
              label={`{{${index + 1}}}`}
              placeholder={t("Value or {{firstname}}")}
              disabled={disabled}
              value={param}
              onChange={(event) => handleParamChange(index, event.target.value)}
            />
          ))}

          <div className={styles.composer__preview}>
            <span className={styles.composer__previewLabel}>{t("Preview")}</span>
            <p className={styles.composer__previewBody}>
              {renderTemplatePreview(
                selectedTemplate.bodyText,
                value.templateParams
              )}
            </p>
          </div>
        </div>
      )}

      {!hideChannel && (
        <Select
          label={t("Channel")}
          options={CHANNEL_OPTIONS.map((option) => ({
            ...option,
            label: t(option.label)
          }))}
          value={value.channel || AUTO_CHANNEL_VALUE}
          disabled={disabled}
          onChange={(selected) => patch({ channel: selected as string })}
        />
      )}

      <span className={styles.composer__hint}>
        {t(
          "Provide both when possible: the backend picks free text inside a contact's 24h window and the approved template outside it. Use {{firstname}} for personalization."
        )}
      </span>
    </div>
  );
};

export default MessageComposer;
